import React, { useState } from 'react'

import ContentBlock from '~/components/content-block'
import DoubleBounceSpinner from '~/components/double-bounce-spinner'
import Modal from '~/components/modal'
import { i18n } from '~/i18n'
import fullName from '~/utils/full-name'

import {
  Heading,
  Label,
  ScrollWrapper,
  TermsContentBlock,
  ContentWrapper,
  FooterWrapper,
  Actions,
  AgreeContentBlock,
  NextButton,
  StyledCheckboxInput,
  ReleasorsHeading,
  Releasors,
  Releasor,
  ReleasorName,
  StyledProfilePicture,
} from './resort-authorization-modal.styles'

import './resort-authorization-modal.scss'

import type { GroupMember, Waiver } from '~/types'

export type BasicProps = {
  signWaiverStatus?: string
  waiverContent: string
  waiverHeading: string
  waiverAccept: string
  participants?: GroupMember[]
  waiver?: Waiver | null | undefined
}

export type ActionProps = {
  onAgreeToggle: (
    arg0: string[],
    waiverID: number | null | undefined,
  ) => void | Promise<void>
  onClose: () => void
}

export type Props = BasicProps & ActionProps

export default function ResortAuthorizationModal({
  waiverAccept,
  waiverHeading,
  waiverContent,
  waiver,
  signWaiverStatus,
  onClose,
  participants = [],
  onAgreeToggle,
}: Props) {
  const [agree, setAgree] = useState(false)

  const isProcessing = signWaiverStatus === 'PROCESSING'

  const handleSubmit = () => {
    const profileIds = participants.map(({ id }) => id)
    onAgreeToggle(profileIds, waiver?.id)
  }

  return (
    <Modal
      title={i18n.t('components.resort_authorization_modal_v2.title')}
      onClose={isProcessing ? undefined : onClose}
      size="large"
      className="amp-resort-authorization-modal-v2"
    >
      <ContentWrapper>
        <Heading>
          <ContentBlock>{waiverHeading}</ContentBlock>
        </Heading>
        {isProcessing ? (
          <DoubleBounceSpinner />
        ) : (
          <>
            {participants && participants.length > 0 && (
              <>
                <ReleasorsHeading>
                  {i18n.t('components.resort_authorization_modal_v2.releasors')}
                </ReleasorsHeading>
                <Releasors className="participants" data-testid="participants">
                  {participants.map((groupMember) => (
                    <Releasor key={groupMember.id}>
                      <StyledProfilePicture
                        alt={`Profile picture for ${groupMember.firstName}`}
                      />
                      <ReleasorName>
                        {fullName(groupMember.firstName, groupMember.lastName)}
                      </ReleasorName>
                    </Releasor>
                  ))}
                </Releasors>
              </>
            )}
            <ScrollWrapper tabIndex={0}>
              <Label>{i18n.t(`waivers.${waiver.kind}.label`)}</Label>
              <TermsContentBlock>{waiverContent}</TermsContentBlock>
            </ScrollWrapper>
          </>
        )}
      </ContentWrapper>
      {!isProcessing && (
        <FooterWrapper>
          <AgreeContentBlock
            className="accept-heading"
            data-testid="accept-heading"
          >
            {waiverAccept}
          </AgreeContentBlock>
          <Actions>
            <StyledCheckboxInput
              id="resort-authorization-modal-next-i-agree"
              label={i18n.t('components.resort_authorization_modal.agree_btn')}
              onChange={() => setAgree((agree) => !agree)}
              checked={agree}
            />
            <NextButton
              id="resort-authorization-modal-next"
              onClick={handleSubmit}
              type="progress"
              disabled={!agree}
            >
              {i18n.t('components.resort_authorization_modal.next_btn')}
            </NextButton>
          </Actions>
        </FooterWrapper>
      )}
    </Modal>
  )
}
